import { CallSplitRounded } from "@mui/icons-material";
import { Box, Stack, Theme, Tooltip, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import { TitledSection } from "components/Classifiers/Common/TitledSection";
import { observer } from "mobx-react";
import React from "react";
import { useStore } from "utils/useStore";
import { LLMClassifierBuilderStore } from "../../Stores/LLMClassifierBuilderStore";
import { ChainSourceStore } from "../../Stores/ChainSourceStore";
import {
    ChainDependentModel,
    ChainGraphModel,
    ChainModelKind,
    ChainSourceModel,
    summarizeOutputNames,
} from "../../Stores/ChainSourceHelpers";
import { describeModel } from "./ChainSourceSection";
import {
    AndSeparator,
    ChainModelIcon,
    ChainNodeVariant,
    NewBadge,
} from "./ChainSourceCommon";

/**
 * Model builder page for a model in the chain - currently only LLM models have a builder page.
 */
function builderPathFor(model: { id: string; kind: ChainModelKind }) {
    return model.kind === "llm"
        ? `/app/classifiers/llm/builder/${model.id}`
        : undefined;
}

/**
 * Describes a child by the outputs of this model it reads. A child that reads
 * none of them is still a dependent, so it gets a plain label instead.
 */
function describeDependency(dependent: ChainDependentModel) {
    return dependent.consumes.length
        ? `depends on ${summarizeOutputNames(dependent.consumes)}`
        : "depends on this model";
}

/**
 * Read-only map of the whole chain this model sits in: every ancestor above and
 * every descendant below, from the model-chain API. Only the direct parents
 * carry gate conditions, since those are the relations this model owns -- what
 * gates the models further out belongs to those models.
 */
export const ModelChainPanel = observer(function () {
    const store = useStore(ChainSourceStore);
    const builderStore = useStore(LLMClassifierBuilderStore);

    const modelName = builderStore.classifier.name || "This model";
    // Furthest ancestor first so the chain reads top down into this model.
    const distantAncestors = store.ancestorLevels
        .filter(({ level }) => level > 1)
        .reverse();
    const distantDescendants = store.descendantLevels.filter(
        ({ level }) => level > 1,
    );

    return (
        <TitledSection
            variant="bordered"
            title={
                <Stack direction="row" columnGap={1} alignItems="center">
                    Model chain
                    <NewBadge />
                </Stack>
            }
            subtitle="A map of where this model sits in the chain and the conditions that gate it."
        >
            <Stack direction="column" width="100%">
                {distantAncestors.map(({ level, models }) => (
                    <ChainLevelGroup
                        key={`ancestors-${level}`}
                        variant="parent"
                        label={`${models.length} ${
                            models.length === 1 ? "MODEL" : "MODELS"
                        } · LEVEL ${level}`}
                        models={models}
                        detail="upstream model"
                    />
                ))}
                {store.parentLevels.map(({ level, parents }) => (
                    <ParentLevel key={level} level={level} parents={parents} />
                ))}
                <ChainNode
                    variant="current"
                    isCurrent
                    name={modelName}
                    detail={`this model · emits ${store.output}`}
                    inputs={store.declaredInputs.map((input) => ({
                        key: input.variableName ?? input.name,
                        label: input.name,
                        isFromParent: !!input.parentId,
                    }))}
                />
                {store.dependents.length > 0 && (
                    <DependentLevel dependents={store.dependents} />
                )}
                {distantDescendants.map(({ level, models }) => (
                    <ChainLevelGroup
                        key={`descendants-${level}`}
                        variant="dependent"
                        label={`${models.length} ${
                            models.length === 1 ? "MODEL" : "MODELS"
                        } · LEVEL ${level} BELOW`}
                        models={models}
                        detail="downstream model"
                    />
                ))}
            </Stack>
        </TitledSection>
    );
});

/**
 * A level of the chain beyond this model's direct relations, drawn like the
 * direct parents so the map reads as one diagram, with the heading saying how
 * far out it sits. It carries no gate: those belong to the models it is wired to.
 */
const ChainLevelGroup = observer(function (props: {
    variant: ChainNodeVariant;
    label: string;
    models: ChainGraphModel[];
    detail: string;
}) {
    return (
        <>
            {props.variant === "dependent" && <ChainConnector />}
            <Stack
                direction="column"
                rowGap={1.25}
                sx={(theme) => ({
                    backgroundColor: theme.palette.gray[50],
                    border: `1px solid ${theme.palette.lightgrayBorder.main}`,
                    borderRadius: "10px",
                    paddingBlock: theme.spacing(1.25),
                    paddingInline: theme.spacing(1.25),
                    width: "100%",
                })}
            >
                <Typography
                    sx={(theme) => ({
                        color: theme.palette.gray[500],
                        fontSize: "11px",
                        fontWeight: 700,
                        letterSpacing: "0.6px",
                        paddingLeft: theme.spacing(0.25),
                    })}
                >
                    {props.label}
                </Typography>
                {props.models.map((model) => (
                    <ChainNode
                        key={model.id}
                        variant={props.variant}
                        name={model.name}
                        to={builderPathFor(model)}
                        detail={
                            model.versionLabel
                                ? `v${model.versionLabel} · ${props.detail}`
                                : props.detail
                        }
                    />
                ))}
            </Stack>
            {props.variant === "parent" && <ChainConnector />}
        </>
    );
});

/**
 * The children directly below this model, boxed together and headed by how many
 * there are once there is more than one. They are not a conjunction the way a
 * level of parents is -- each gates on this model for itself -- so no operator
 * sits between them and the edge stays plain.
 */
const DependentLevel = observer(function (props: {
    dependents: ChainDependentModel[];
}) {
    const { dependents } = props;
    const cards = dependents.map((dependent) => (
        <ChainNode
            key={dependent.id}
            variant="dependent"
            name={dependent.name}
            to={builderPathFor(dependent)}
            detail={describeDependency(dependent)}
        />
    ));

    // A lone child has nothing to group, and its heading would only restate what
    // the one card already says, so it stands on the edge by itself.
    if (dependents.length === 1) {
        return (
            <>
                <ChainConnector />
                {cards}
            </>
        );
    }

    return (
        <>
            <ChainConnector />
            <Stack
                direction="column"
                rowGap={1.25}
                sx={(theme) => ({
                    backgroundColor: theme.palette.purple[50],
                    border: `1px solid ${theme.palette.purple[100]}`,
                    borderRadius: "10px",
                    paddingBlock: theme.spacing(1.25),
                    paddingInline: theme.spacing(1.25),
                    width: "100%",
                })}
            >
                <Stack
                    direction="row"
                    columnGap={0.75}
                    alignItems="center"
                    paddingLeft={0.25}
                >
                    <CallSplitRounded
                        sx={(theme) => ({
                            color: theme.palette.purple[500],
                            fontSize: "14px",
                            rotate: "180deg",
                        })}
                    />
                    <Typography
                        sx={(theme) => ({
                            color: theme.palette.purple[500],
                            fontSize: "11px",
                            fontWeight: 700,
                            letterSpacing: "0.6px",
                        })}
                    >
                        {dependents.length} CHILDREN · LEVEL 1
                    </Typography>
                </Stack>
                {cards}
            </Stack>
        </>
    );
});

/**
 * One level of parents above this model, boxed together and headed by how many
 * there are. Peers have their gates ANDed, since all of them have to pass: each
 * card states its own gate and the edge below summarizes them. A lone parent has
 * nothing to summarize, so its gate goes on the edge instead.
 */
const ParentLevel = observer(function (props: {
    level: number;
    parents: ChainSourceModel[];
}) {
    const store = useStore(ChainSourceStore);
    const { level, parents } = props;
    const isSingleParent = parents.length === 1;

    return (
        <>
            <Stack
                direction="column"
                rowGap={1.25}
                sx={(theme) => ({
                    backgroundColor: theme.palette.primary[50],
                    border: `1px solid ${theme.palette.primary[200]}`,
                    borderRadius: "10px",
                    paddingBlock: theme.spacing(1.25),
                    paddingInline: theme.spacing(1.25),
                    width: "100%",
                })}
            >
                <Stack
                    direction="row"
                    columnGap={0.75}
                    alignItems="center"
                    paddingLeft={0.25}
                >
                    <CallSplitRounded
                        sx={(theme) => ({
                            color: theme.palette.primary[500],
                            fontSize: "14px",
                        })}
                    />
                    <Typography
                        sx={(theme) => ({
                            color: theme.palette.primary[500],
                            fontSize: "11px",
                            fontWeight: 700,
                            letterSpacing: "0.6px",
                        })}
                    >
                        {parents.length}{" "}
                        {parents.length === 1 ? "PARENT" : "PARENTS"} · LEVEL{" "}
                        {level}
                    </Typography>
                </Stack>
                {parents.map((parent, index) => (
                    <React.Fragment key={parent.id}>
                        {index > 0 && <AndSeparator />}
                        <ChainNode
                            variant="parent"
                            name={parent.name}
                            to={builderPathFor(parent)}
                            // A lone parent's gate is on the edge, leaving the
                            // card to describe the model.
                            detail={
                                isSingleParent
                                    ? describeModel(parent)
                                    : store.describeGate(parent.id) ??
                                      describeModel(parent)
                            }
                        />
                    </React.Fragment>
                ))}
            </Stack>
            {isSingleParent ? (
                <ChainConnector label={store.describeGate(parents[0].id)} />
            ) : (
                <ChainConnector
                    label={
                        parents.length === 2
                            ? "both conditions met"
                            : `all ${parents.length} conditions met`
                    }
                />
            )}
        </>
    );
});

const nodeNameSx = (theme: Theme) => ({
    color: theme.palette.gray[700],
    fontSize: "15px",
    fontWeight: 700,
    // Model names are user supplied and can run long without a space to break at.
    overflowWrap: "anywhere" as const,
});

function ChainNode(props: {
    variant: ChainNodeVariant;
    name: string;
    detail: string;
    level?: number;
    inputs?: { key: string; label: string; isFromParent?: boolean }[];
    isCurrent?: boolean;
    to?: string;
}) {
    return (
        <Box
            sx={(theme) => ({
                backgroundColor: props.isCurrent
                    ? theme.palette.primary[50]
                    : theme.palette.white.main,
                border: props.isCurrent
                    ? `2px solid ${theme.palette.primary[300]}`
                    : `1px solid ${theme.palette.lightgrayBorder.main}`,
                borderRadius: "10px",
                paddingBlock: theme.spacing(1.5),
                paddingInline: theme.spacing(1.5),
                width: "100%",
            })}
        >
            <Stack direction="row" columnGap={1.25} alignItems="center">
                <ChainModelIcon variant={props.variant} size={30} />
                {props.to ? (
                    // New tab so following the chain never discards unsaved
                    // work on the model being edited.
                    <Tooltip title="Open this model in Model Builder in a new tab">
                        <Link
                            className="pendo-ignore"
                            to={props.to}
                            target="_blank"
                            rel="noopener"
                            style={{ textDecoration: "none" }}
                        >
                            <Typography
                                sx={[
                                    nodeNameSx,
                                    {
                                        "&:hover": {
                                            textDecoration: "underline",
                                        },
                                    },
                                ]}
                            >
                                {props.name}
                            </Typography>
                        </Link>
                    </Tooltip>
                ) : (
                    <Typography sx={nodeNameSx}>{props.name}</Typography>
                )}
                <Box flexGrow={1} />
                {props.level !== undefined && (
                    <Box
                        sx={(theme) => ({
                            backgroundColor: theme.palette.primary[100],
                            borderRadius: "4px",
                            color: theme.palette.primary[700],
                            fontSize: "11px",
                            fontWeight: 600,
                            paddingInline: theme.spacing(0.75),
                            whiteSpace: "nowrap",
                        })}
                    >
                        Level {props.level}
                    </Box>
                )}
            </Stack>
            <Typography
                sx={(theme) => ({
                    color: theme.palette.gray[500],
                    fontSize: "12px",
                    marginTop: theme.spacing(0.5),
                    overflowWrap: "anywhere",
                })}
            >
                {props.detail}
            </Typography>
            {!!props.inputs?.length && (
                <Stack
                    direction="row"
                    columnGap={0.5}
                    rowGap={0.5}
                    flexWrap="wrap"
                    marginTop={0.75}
                >
                    {props.inputs.map((input) => (
                        <Box
                            key={input.key}
                            sx={(theme) => ({
                                backgroundColor: input.isFromParent
                                    ? theme.palette.blue.background
                                    : theme.palette.gray[100],
                                borderRadius: "4px",
                                color: input.isFromParent
                                    ? theme.palette.primary[700]
                                    : theme.palette.gray[600],
                                fontFamily: "monospace",
                                fontSize: "11px",
                                paddingBlock: theme.spacing(0.25),
                                paddingInline: theme.spacing(0.75),
                            })}
                        >
                            {input.label}
                        </Box>
                    ))}
                </Stack>
            )}
        </Box>
    );
}

/** Without a label this is just the line joining two nodes. */
function ChainConnector(props: { label?: string }) {
    return (
        <Stack direction="column" alignItems="center" width="100%">
            <Box
                sx={(theme) => ({
                    borderLeft: `1px solid ${theme.palette.primary[200]}`,
                    height: theme.spacing(props.label ? 1 : 2),
                })}
            />
            {props.label && (
                <>
                    <Box
                        sx={(theme) => ({
                            backgroundColor: theme.palette.blue.background,
                            borderRadius: "10px",
                            color: theme.palette.primary[700],
                            fontSize: "11px",
                            paddingBlock: theme.spacing(0.25),
                            paddingInline: theme.spacing(1),
                            textAlign: "center",
                        })}
                    >
                        {props.label}
                    </Box>
                    <Box
                        sx={(theme) => ({
                            borderLeft: `1px solid ${theme.palette.primary[200]}`,
                            height: theme.spacing(1),
                        })}
                    />
                </>
            )}
        </Stack>
    );
}
